import React, { useState } from "react";
import { Form, Modal, Row, Button } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import FarmBusinessPopup from "components/siteRegistration/FarmBusinessPopup";
import { SITE_REGISTRATION_LOTTIE_OPTIONS } from "configurations/Constants";
import BackDropLoader from "components/common/BackDropLoader";
import Lottie from "react-lottie";
import SiteService from "services/SiteService";
import {
  setSaveSiteSuccess,
  setSavedSite,
  setSelectedSite,
  setSites,
} from "reduxStore/slices/siteSlice";
import {
  setBusinesses,
  setFarmsBusinesses,
} from "reduxStore/slices/globalSlice";
import {
  SITE_UPDATE_ERROR,
  PLEASE_ENTER_SITE_NAME,
  PLEASE_SELECT_FARM,
  PLEASE_ENTER_TOTAL_ACRES,
  PLEASE_ENTER_FALLOW_ACRES,
  FALLOW_ACRES_CANNOT_EXCEED_TOTAL_ACRES,
  SITE_NAME_ALREADY_EXISTS,
  LABEL_ENTER_SITE_NAME,
  LABEL_ENTER_TOTAL_ACRES,
  LABEL_ENTER_FALLOW_ACRES,
  LABEL_SELECT_FARM,
  TOTAL_ACRES_MUST_BE_GREATER_THAN_ZERO,
  VIEW_SITE,
  EDIT_SITE,
  NEW_FARM,
} from "configurations/MessageConstants";
import { toast } from "react-toastify";
import { InputHelper } from "utils/InputHelper";

const EditSite = ({ site, show, setShowEditSite }) => {
  const dispatch = useDispatch();
  const [showFarmModal, setShowFarmModal] = useState(false);

  const businessList = useSelector((state) => state.global.businesses);
  const farmBusinessList = useSelector((state) => state.global.farmsBusinesses);
  const sites = useSelector((state) => state.site.sites);
  const isSharedViewOnly = site.isShared && !site.editor;

  const defaultValues = {
    siteName: site.name,
    farmName: site.farmName,
    businessName: site.businessName,
    totalArea: site.totalArea,
    fallowArea: site.fallowArea,
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm({ defaultValues });

  const totalArea = watch("totalArea");

  const handleNewFarm = () => {
    setShowFarmModal(true);
  };

  const handleCloseFarmBusinessPopup = () => {
    setShowFarmModal(false);
  };

  const handleAddFarmBusiness = (farmData) => {
    setValue("farmName", farmData.farm);
    setValue("businessName", farmData.business);
    const newFarmBusiness = {
      farmName: farmData.farm,
      businessName: farmData.business,
    };
    if (!farmBusinessList.some((farm) => farm.farmName === farmData.farm)) {
      dispatch(setFarmsBusinesses([...farmBusinessList, newFarmBusiness]));
    }
    if (!businessList.some((business) => business === farmData.business)) {
      dispatch(setBusinesses([...businessList, farmData.business]));
    }
    setShowFarmModal(false);
  };

  const onSubmit = async (data) => {
    try {
      const updatedSite = {
        id: site.id,
        name: data.siteName,
        farmName: data.farmName,
        businessName: data.businessName,
        totalArea: data.totalArea,
        fallowArea: data.fallowArea,
      };
      const response = await SiteService.updateSite(updatedSite);
      handleSave(response);
      dispatch(
        setSites(
          sites.map((site) => (site.id === response.id ? response : site))
        )
      );
      setShowEditSite(false);
    } catch (error) {
      console.error(SITE_UPDATE_ERROR, error);
      toast.error(error.response.data.detail);
    }
  };

  const handleSave = async (site) => {
    dispatch(setSavedSite(site));
    dispatch(setSelectedSite(site));
    dispatch(setSaveSiteSuccess(true));
  };

  return (
    <>
      {isSubmitting && (
        <BackDropLoader
          loader={
            <Lottie
              options={SITE_REGISTRATION_LOTTIE_OPTIONS}
              height={200}
              width={200}
            />
          }
        />
      )}
      <Modal
        show={show && !showFarmModal}
        onHide={() => setShowEditSite(false)}
        centered
        size="md"
        animation={false}
        aria-labelledby="edit-site-popup"
      >
        <Modal.Header closeButton>
          <Modal.Title id="edit-site-popup">
            {isSharedViewOnly ? VIEW_SITE : EDIT_SITE}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            <Row className="g-3">
              <Form.Group controlId="formSiteName">
                <Form.Label>Site Name</Form.Label>
                <Controller
                  name="siteName"
                  control={control}
                  rules={{
                    required: PLEASE_ENTER_SITE_NAME,
                    validate: (value) => {
                      const siteExists = sites.some(
                        (s) =>
                          s.id !== site.id &&
                          s.name.toLowerCase() === value.toLowerCase()
                      );
                      return !siteExists || SITE_NAME_ALREADY_EXISTS;
                    },
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      className="input-underline"
                      type="text"
                      placeholder={LABEL_ENTER_SITE_NAME}
                      isInvalid={!!errors.siteName}
                      maxLength={50}
                      disabled={isSharedViewOnly}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.siteName?.message}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="formFarm">
                <Form.Label className="d-flex justify-content-between">
                  <span>Farm</span>
                  <Button
                    variant="link"
                    className="new-farm-button"
                    onClick={handleNewFarm}
                    type="button"
                    disabled={isSharedViewOnly}
                  >
                    <i className="fa-solid fa-circle-plus me-1" />
                    <small>{NEW_FARM}</small>
                  </Button>
                </Form.Label>
                <Controller
                  name="farmName"
                  control={control}
                  rules={{ required: PLEASE_SELECT_FARM }}
                  render={({ field }) => (
                    <Form.Select
                      {...field}
                      className="input-underline"
                      aria-label="Select Farm"
                      isInvalid={!!errors.farmName}
                      onChange={(e) => {
                        field.onChange(e);
                        const selectedFarm = farmBusinessList.find(
                          (farm) => farm.farmName === e.target.value
                        );
                        setValue("businessName", selectedFarm?.businessName);
                      }}
                      disabled={isSharedViewOnly}
                    >
                      <option value="">{LABEL_SELECT_FARM}</option>
                      {[
                        ...new Set(
                          farmBusinessList.map((farm) => farm.farmName)
                        ),
                      ].map((farmName) => (
                        <option key={farmName} value={farmName}>
                          {farmName}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.farmName?.message}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="formTotalAcres">
                <Form.Label>Total Acres</Form.Label>
                <Controller
                  name="totalArea"
                  control={control}
                  rules={{
                    required: PLEASE_ENTER_TOTAL_ACRES,
                    validate: (value) =>
                      Number(value) > 0 ||
                      TOTAL_ACRES_MUST_BE_GREATER_THAN_ZERO,
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      className="input-underline"
                      type="text"
                      placeholder={LABEL_ENTER_TOTAL_ACRES}
                      isInvalid={!!errors.totalArea}
                      maxLength={10}
                      onChange={(e) => {
                        InputHelper.validateFloatNumberInput(e, field.onChange);
                      }}
                      disabled={isSharedViewOnly}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.totalArea?.message}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="formFallowAcres">
                <Form.Label>Fallow Acres</Form.Label>
                <Controller
                  name="fallowArea"
                  control={control}
                  rules={{
                    required: PLEASE_ENTER_FALLOW_ACRES,
                    validate: (value) =>
                      Number(value) <= Number(totalArea) ||
                      FALLOW_ACRES_CANNOT_EXCEED_TOTAL_ACRES,
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      className="input-underline"
                      type="text"
                      placeholder={LABEL_ENTER_FALLOW_ACRES}
                      isInvalid={!!errors.fallowArea}
                      maxLength={10}
                      onChange={(e) => {
                        InputHelper.validateFloatNumberInput(e, field.onChange);
                      }}
                      disabled={isSharedViewOnly}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.fallowArea?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>
          </Modal.Body>
          {!isSharedViewOnly && (
            <Modal.Footer>
              <Button
                variant="outline-secondary"
                onClick={() => setShowEditSite(false)}
                type="button"
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                Update
              </Button>
            </Modal.Footer>
          )}
        </Form>
      </Modal>
      <FarmBusinessPopup
        showFarmModal={showFarmModal}
        businessList={businessList}
        farmBusinessList={farmBusinessList}
        onAdd={handleAddFarmBusiness}
        onClose={handleCloseFarmBusinessPopup}
      />
    </>
  );
};

export default EditSite;
